'use client';

/**
 * LandingReveal — the round-3 below-fold entrance grammar (r3-judge-F-verdict.md
 * Decision 5, r3-debate-rulings.md Harmonization f, MOTION-CONTRACT finding #3).
 * Replaces the legacy `ScrollReveal` (30px/600ms/--ease-reveal) on the landing
 * page's own section files ONLY — `ScrollReveal` itself is untouched and stays
 * in place for its other (preview feature) consumers.
 *
 * Visual contract: opacity + 12px translateY, 420ms, LANDING_EASE.enter — driven
 * entirely by the `.landing-reveal` CSS entrance MOTION lane already declared in
 * globals.css (`--motion-enter`/`--ease-enter`, single numeric source is
 * `landing-motion.ts`). That class had zero consumers until this component.
 * Reduced motion swaps to a 320ms opacity-only fade via the EXISTING
 * `@media (prefers-reduced-motion: reduce) .landing-motion-scope .landing-reveal`
 * override — a CSS-level concern, not a JS branch (same split NarrativeHandoff
 * documents: "IO triggering stays alive; reduced motion is a CSS-level concern,
 * not a JS bypass").
 *
 * `.landing-reveal` only paints via the descendant selector
 * `.landing-motion-scope .landing-reveal`, so this component renders two nested
 * divs: the OUTER carries `landing-motion-scope` + the caller's `className`
 * (the real grid/flex item — occupies the exact slot ScrollReveal's single div
 * used to), the INNER carries the entrance class and is a plain, layout-inert
 * wrapper (`h-full` so it still propagates a stretched grid-row height to
 * children like `ProofCard`'s `h-full`).
 *
 * Trigger + resilience: reuses the proven `useScrollReveal` IO hook (the same
 * one `ScrollReveal` itself is built on) plus its identical genuine-failure
 * watchdog. SSR/pre-mount output never carries the `.landing-reveal` class —
 * never a bare `opacity:0` `both`-fill keyframe with no JS guard, the hero-CTA
 * incident's exact failure mode ([[incidents/hero-cta-fade-in-regression]]) —
 * the class is only ever added once JS confirms a real intersection, or, if
 * IntersectionObserver is unsupported or has genuinely failed to report an
 * element already on/near screen after 4s, via the same fallback. Plays once.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { cn } from '@/lib/utils';
import { useScrollReveal } from '@/features/preview/hooks/useScrollReveal';
import { LANDING_INVIEW } from '../motion/landing-motion';

function subscribeIOSupport() {
  return () => {};
}
function getIOSupportSnapshot() {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}
function getIOSupportServerSnapshot() {
  return true;
}

interface LandingRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

export default function LandingReveal({ children, className, delay = 0 }: LandingRevealProps) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({
    threshold: LANDING_INVIEW.entrance.amount,
    rootMargin: LANDING_INVIEW.entrance.margin,
  });
  const [hasAnimated, setHasAnimated] = useState(false);

  const ioSupported = useSyncExternalStore(
    subscribeIOSupport,
    getIOSupportSnapshot,
    getIOSupportServerSnapshot
  );

  // Genuine-failure fallback (identical contract to ScrollReveal's own): only
  // ever forces visibility when IO can't or didn't do its job.
  const [timerExpired, setTimerExpired] = useState(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isVisible) return;

    if (!ioSupported) {
      setTimerExpired(true);
      return;
    }

    fallbackTimerRef.current = setTimeout(() => {
      const rect = ref.current?.getBoundingClientRect();
      const nearViewport = !!rect && rect.top < window.innerHeight && rect.bottom > 0;
      if (nearViewport) {
        setTimerExpired(true);
      }
    }, 4000);

    return () => {
      if (fallbackTimerRef.current !== null) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [ioSupported, isVisible, ref]);

  const handleAnimationEnd = useCallback(() => setHasAnimated(true), []);

  const revealed = isVisible || timerExpired;
  const innerClass = hasAnimated ? 'opacity-100' : revealed ? 'landing-reveal' : 'opacity-0';
  const style = revealed && !hasAnimated ? { animationDelay: `${delay}ms` } : undefined;

  return (
    <div ref={ref} className={cn('landing-motion-scope', className)}>
      <div className={cn('h-full', innerClass)} style={style} onAnimationEnd={handleAnimationEnd}>
        {children}
      </div>
    </div>
  );
}
